import { supabase } from './supabase';

/*
  "Tell me when it is free."

  **Built 10 September 2026 for job 25**, and it is the owner's own answer to
  Group H's question 2. Asked whether a rep who finds somebody else in a count
  sheet should be able to ask for it or be told when it frees, he said **"maybe
  both."**

  **The first of the two already existed** and is not rebuilt here: OnHand has
  an ownership request that goes to an administrator, who decides. This is the
  second, which was new.

  -- How it works, and what it does not do ------------------------------------

  A sheet is held by whoever has it open, and the hold expires thirty minutes
  after they last touch it. **Nothing in OnHand runs on a timer in the
  database**, so there is nothing to notice the moment a hold lapses and send a
  message out of its own accord.

  So the watch is kept on the rep's own device and OnHand checks it while it is
  open: every half minute it asks whether any watched sheet has come free, and
  the moment one has it says so and stops watching it.

  **What that means in plain terms, said here rather than discovered later.**
  The rep is told while OnHand is open, including as soon as they next open it.
  They are not told when OnHand is closed, because a website that is not running
  cannot tell anybody anything. **A message that arrives on a closed phone needs
  the app-store version the owner is planning**, which is on the queue as its own
  job, and this is written the way it is so that it becomes the obvious place to
  hang that when it comes.

  **It is on the device rather than in the database on purpose too.** A rep
  waiting for a sheet is going to go and count it on the thing in their hand.
*/

const KEY = 'onhand.sheetwatch.v1';
const EVERY = 30000;

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function write(list) {
  try { window.localStorage.setItem(KEY, JSON.stringify(list)); return true; }
  catch { return false; }
}

export function watching(countId) {
  return read().some(w => w.countId === countId);
}

/*
  Start watching. Answers false when the device will not remember it, so the
  screen can say so rather than showing a tick over nothing. **A watch nobody
  can keep is worse than no watch**, because the rep stops checking.
*/
export function watch(countId, name) {
  const list = read().filter(w => w.countId !== countId);
  list.push({ countId, name, since: new Date().toISOString() });
  return write(list);
}

export function unwatch(countId) {
  write(read().filter(w => w.countId !== countId));
}

/*
  Ask whether any watched sheet has come free.

  **A sheet is free when nobody has it open**, which is `session_user_id` being
  empty, or when whoever has it has not touched it for thirty minutes, which is
  the same rule count entry itself uses to decide whether to let somebody in.

  **A read that fails tells you nothing about the sheet**, so it is left alone
  and asked again in half a minute. Treating a network hiccup as "it is free
  now" would send a rep to a sheet somebody is still typing into.
*/
export async function check() {
  const list = read();
  if (list.length === 0) return [];
  const ids = list.map(w => w.countId);
  const { data, error } = await supabase
    .from('inventory_counts')
    .select('id, session_user_id, session_started_at, status')
    .in('id', ids);
  if (error || !data) return [];

  const stale = Date.now() - 30 * 60000;
  const freed = [];
  for (const row of data) {
    const held = row.session_user_id
      && row.session_started_at
      && new Date(row.session_started_at).getTime() > stale;
    if (!held) {
      const w = list.find(x => x.countId === row.id);
      if (w) freed.push(w);
    }
  }
  freed.forEach(w => unwatch(w.countId));
  return freed;
}

/*
  Watch while OnHand is open. Returns the stop, so a screen can start it and
  React can end it.
*/
export function startWatching(onFree) {
  if (typeof window === 'undefined') return () => {};
  let stopped = false;
  const tick = async () => {
    if (stopped) return;
    const freed = await check();
    if (!stopped) freed.forEach(onFree);
  };
  const timer = setInterval(tick, EVERY);
  tick();
  return () => { stopped = true; clearInterval(timer); };
}
